import type { ShelfField } from './ShelvesPanel'

export type QuickBooksGlCreatorVisualizationType = 'table' | 'bar' | 'line'
export type QuickBooksGlCreatorAggregation = 'SUM' | 'AVG' | 'MIN' | 'MAX' | 'COUNT'
export type QuickBooksGlCreatorDateGrain = 'year' | 'quarter' | 'month' | 'week' | 'day'

export type QuickBooksGlCreatorConfig = {
  rows: ShelfField[]
  columns: ShelfField[]
  measure: string
  aggregation: QuickBooksGlCreatorAggregation
  dateGrains: Record<string, QuickBooksGlCreatorDateGrain>
  visualizationType: QuickBooksGlCreatorVisualizationType
}

export type QuickBooksGlCreatorSavedView = QuickBooksGlCreatorConfig & {
  id: string
  name: string
  createdAt: string
  updatedAt: string
}

export type QuickBooksGlCreatorField = {
  id: string
  label: string
  role: 'dimension' | 'measure'
  dataType: 'string' | 'number' | 'date' | 'boolean'
}

export type QuickBooksGlCreatorQuery = {
  sql: string
  dimensionAliases: string[]
  measureAlias: string
}

export const QUICKBOOKS_GL_CREATOR_TABLE = 'fg_qbo_fpa_gl_detail'
export const QUICKBOOKS_GL_CREATOR_DATASOURCE_ID = 'qbo_gl_detail'
export const QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME = 'GL Detail'
export const QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME = 'GL Detail'
export const QUICKBOOKS_GL_CREATOR_PREVIEW_LIMIT = 5000
export const QUICKBOOKS_GL_CREATOR_DEFAULT_CONFIG_VERSION = 2
export const QUICKBOOKS_GL_CREATOR_PENDING_OPEN_STORAGE_KEY = 'fg_quickbooks_gl_creator_pending_open_v1'
export const QUICKBOOKS_GL_CREATOR_PENDING_OPEN_MAX_AGE_MS = 2 * 60 * 1000
export const DRAG_MIME_V1 = 'application/x-fusegrid-field-v1'
export const DATE_GRAINS: QuickBooksGlCreatorDateGrain[] = ['year', 'quarter', 'month', 'week', 'day']
export const AGGREGATIONS: QuickBooksGlCreatorAggregation[] = ['SUM', 'AVG', 'MIN', 'MAX', 'COUNT']

export const QUICKBOOKS_GL_CREATOR_FIELDS: QuickBooksGlCreatorField[] = [
  { id: 'txn_date', label: 'Date', role: 'dimension', dataType: 'date' },
  { id: 'account', label: 'Account', role: 'dimension', dataType: 'string' },
  { id: 'account_name', label: 'Account name', role: 'dimension', dataType: 'string' },
  { id: 'account_number', label: 'Account number', role: 'dimension', dataType: 'string' },
  { id: 'transaction_type', label: 'Type', role: 'dimension', dataType: 'string' },
  { id: 'name', label: 'Name', role: 'dimension', dataType: 'string' },
  { id: 'customer', label: 'Customer', role: 'dimension', dataType: 'string' },
  { id: 'vendor', label: 'Vendor', role: 'dimension', dataType: 'string' },
  { id: 'employee', label: 'Employee', role: 'dimension', dataType: 'string' },
  { id: 'class', label: 'Class', role: 'dimension', dataType: 'string' },
  { id: 'department', label: 'Location', role: 'dimension', dataType: 'string' },
  { id: 'product_service', label: 'Item/Product', role: 'dimension', dataType: 'string' },
  { id: 'split_account', label: 'Split account', role: 'dimension', dataType: 'string' },
  { id: 'source', label: 'Source', role: 'dimension', dataType: 'string' },
  { id: 'is_adjustment', label: 'Adjustment', role: 'dimension', dataType: 'boolean' },
  { id: 'amount', label: 'Amount', role: 'measure', dataType: 'number' },
  { id: 'debit', label: 'Debit', role: 'measure', dataType: 'number' },
  { id: 'credit', label: 'Credit', role: 'measure', dataType: 'number' },
  { id: 'net_amount', label: 'Net amount', role: 'measure', dataType: 'number' },
  { id: 'tax_amount', label: 'Tax amount', role: 'measure', dataType: 'number' },
  { id: 'quantity', label: 'Quantity', role: 'measure', dataType: 'number' },
  { id: 'rate', label: 'Rate', role: 'measure', dataType: 'number' },
  { id: 'open_balance', label: 'Open balance', role: 'measure', dataType: 'number' },
]

export const FIELD_BY_ID = new Map(QUICKBOOKS_GL_CREATOR_FIELDS.map((field) => [field.id, field]))

const QUICKBOOKS_GL_CANONICAL_SUPPORT_FIELDS: QuickBooksGlCreatorField[] = [
  { id: 'num', label: 'Num', role: 'dimension', dataType: 'string' },
  { id: 'account_id', label: 'Account ID', role: 'dimension', dataType: 'string' },
  { id: 'customer_id', label: 'Customer ID', role: 'dimension', dataType: 'string' },
  { id: 'vendor_id', label: 'Vendor ID', role: 'dimension', dataType: 'string' },
  { id: 'employee_id', label: 'Employee ID', role: 'dimension', dataType: 'string' },
  { id: 'class_id', label: 'Class ID', role: 'dimension', dataType: 'string' },
  { id: 'location', label: 'Location', role: 'dimension', dataType: 'string' },
  { id: 'department_id', label: 'Department ID', role: 'dimension', dataType: 'string' },
  { id: 'item', label: 'Item', role: 'dimension', dataType: 'string' },
  { id: 'item_id', label: 'Item ID', role: 'dimension', dataType: 'string' },
  { id: 'invoice_id', label: 'Invoice ID', role: 'dimension', dataType: 'string' },
  { id: 'invoice_num', label: 'Invoice Num', role: 'dimension', dataType: 'string' },
  { id: 'invoice_date', label: 'Invoice Date', role: 'dimension', dataType: 'date' },
  { id: 'invoice_due_date', label: 'Invoice Due Date', role: 'dimension', dataType: 'date' },
  { id: 'sales_term_id', label: 'Sales Term ID', role: 'dimension', dataType: 'string' },
  { id: 'sales_term_name', label: 'Sales Term', role: 'dimension', dataType: 'string' },
  { id: 'term_days', label: 'Term Days', role: 'measure', dataType: 'number' },
  { id: 'terms_source', label: 'Terms Source', role: 'dimension', dataType: 'string' },
  { id: 'ar_paid', label: 'A/R Paid', role: 'dimension', dataType: 'string' },
  { id: 'memo', label: 'Memo', role: 'dimension', dataType: 'string' },
  { id: 'tax_code', label: 'Tax Code', role: 'dimension', dataType: 'string' },
]

const QUICKBOOKS_GL_CANONICAL_SOURCE_FIELDS: QuickBooksGlCreatorField[] = [
  ...QUICKBOOKS_GL_CREATOR_FIELDS,
  ...QUICKBOOKS_GL_CANONICAL_SUPPORT_FIELDS.filter((field) => !FIELD_BY_ID.has(field.id)),
]

const QUICKBOOKS_GL_SOURCE_COLUMN_ALIASES: Record<string, string[]> = {
  txn_date: ['Date', 'TxnDate', 'Txn Date', 'TransactionDate', 'Transaction Date'],
  transaction_type: ['TransactionType', 'Transaction Type', 'Type', 'TxnType', 'Txn Type'],
  num: ['Num', 'DocNumber', 'Doc Number', 'DocumentNumber', 'Document Number'],
  name: ['Name', 'Entity', 'Payee'],
  account: ['Account'],
  account_name: ['AccountName', 'Account Name', 'Account_Name'],
  account_id: ['AccountId', 'Account ID', 'AccountID', 'Account_Id'],
  account_number: ['AccountNumber', 'Account Number', 'AccountNum', 'Account Num', 'Account_Number'],
  customer: ['Customer', 'CustomerName', 'Customer Name', 'Customer_Name', 'CustomerFullName', 'Customer Full Name'],
  customer_id: ['CustomerId', 'Customer ID', 'CustomerID', 'Customer_Id'],
  vendor: ['Vendor', 'VendorName', 'Vendor Name', 'Vendor_Name'],
  vendor_id: ['VendorId', 'Vendor ID', 'VendorID', 'Vendor_Id'],
  employee: ['Employee', 'EmployeeName', 'Employee Name', 'Employee_Name'],
  employee_id: ['EmployeeId', 'Employee ID', 'EmployeeID', 'Employee_Id'],
  class: ['Class', 'ClassName', 'Class Name', 'Class_Name', 'Klass', 'KlassName'],
  class_id: ['ClassId', 'Class ID', 'ClassID', 'Class_Id', 'KlassId', 'Klass ID'],
  department: ['Department', 'Location', 'DepartmentName', 'LocationName', 'Location Name'],
  location: ['Location', 'Department', 'LocationName', 'Location Name', 'DepartmentName'],
  department_id: ['DepartmentId', 'Department ID', 'DepartmentID', 'Department_Id', 'LocationId', 'Location ID'],
  product_service: ['Product_Service', 'ProductService', 'Product Service', 'Product/Service', 'Item', 'ItemName', 'Item Name'],
  item: ['Item', 'ItemName', 'Item Name', 'Product_Service', 'ProductService', 'Product Service', 'Product/Service'],
  item_id: ['ItemId', 'Item ID', 'ItemID', 'Item_Id', 'ProductServiceId', 'Product Service ID', 'Product_ServiceId'],
  split_account: ['SplitAccount', 'Split Account', 'Split'],
  invoice_id: ['InvoiceId', 'Invoice ID', 'InvoiceID', 'Invoice_Id'],
  invoice_num: ['InvoiceNum', 'Invoice Num', 'InvoiceNumber', 'Invoice Number', 'DocNumber', 'Doc Number'],
  invoice_date: ['InvoiceDate', 'Invoice Date', 'InvoiceTxnDate', 'Invoice Txn Date'],
  invoice_due_date: ['InvoiceDueDate', 'Invoice Due Date', 'DueDate', 'Due Date'],
  sales_term_id: ['SalesTermId', 'Sales Term ID', 'SalesTermID'],
  sales_term_name: ['SalesTermName', 'Sales Term Name', 'Terms', 'TermName'],
  term_days: ['TermDays', 'Term Days'],
  terms_source: ['TermsSource', 'Terms Source'],
  ar_paid: ['ARPaid', 'A/R Paid', 'A_RPaid'],
  memo: ['Memo', 'Description'],
  tax_code: ['TaxCode', 'Tax Code'],
  source: ['Source'],
  is_adjustment: ['IsAdjustment', 'Is Adjustment', 'Adjustment'],
  amount: ['Amount'],
  debit: ['Debit'],
  credit: ['Credit'],
  net_amount: ['NetAmount', 'Net Amount', 'Net_Amount'],
  tax_amount: ['TaxAmount', 'Tax Amount', 'Tax_Amount'],
  quantity: ['Quantity', 'Qty'],
  rate: ['Rate'],
  open_balance: ['OpenBalance', 'Open Balance', 'Open_Balance'],
}

type QuickBooksGlCreatorSourceColumn = string | { name?: string | null; column_name?: string | null }

function deepCloneSafe<T>(value: T): T {
  try {
    return JSON.parse(JSON.stringify(value))
  } catch {
    return value
  }
}

function quoteDuckDbIdent(raw: string): string {
  return `"${String(raw || '').replace(/"/g, '""')}"`
}

function quoteDuckDbRelation(raw: string): string {
  return String(raw || '')
    .split('.')
    .map((part) => quoteDuckDbIdent(part))
    .join('.')
}

function sourceColumnName(raw: QuickBooksGlCreatorSourceColumn): string {
  return String(typeof raw === 'string' ? raw : (raw?.name ?? raw?.column_name ?? '')).trim()
}

function normalizeQuickBooksGlSourceColumnLookupKey(raw: any): string {
  return String(raw ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function buildSourceColumnLookup(sourceColumns?: QuickBooksGlCreatorSourceColumn[] | null): Map<string, string> | null {
  if (!Array.isArray(sourceColumns)) return null
  const lookup = new Map<string, string>()
  for (const raw of sourceColumns) {
    const name = sourceColumnName(raw)
    if (!name) continue
    const key = name.toLowerCase()
    if (!lookup.has(key)) lookup.set(key, name)
    const compactKey = normalizeQuickBooksGlSourceColumnLookupKey(name)
    if (compactKey && !lookup.has(compactKey)) lookup.set(compactKey, name)
  }
  return lookup
}

function resolveQuickBooksGlSourceColumn(
  lookup: Map<string, string> | null,
  fieldId: string,
  aliases: string[] = [],
): string | null {
  if (!lookup) return fieldId
  for (const candidate of [fieldId, ...aliases]) {
    const exactKey = String(candidate || '').toLowerCase()
    const found = lookup.get(exactKey) || lookup.get(normalizeQuickBooksGlSourceColumnLookupKey(candidate))
    if (found) return found
  }
  return null
}

function missingQuickBooksGlFieldSql(field: QuickBooksGlCreatorField, lookup: Map<string, string>): string {
  if (field.id === 'account_name') {
    const accountColumn = resolveQuickBooksGlSourceColumn(lookup, 'account', ['Account'])
    if (accountColumn) return quoteDuckDbIdent(accountColumn)
  }
  if (field.id === 'source') return "'QuickBooksGL'"
  if (field.id === 'is_adjustment') return 'FALSE'
  if (field.dataType === 'number') return '0'
  if (field.dataType === 'boolean') return 'FALSE'
  if (field.dataType === 'date') return 'NULL'
  return "''"
}

function creatorDataTypeForField(dataType: string | null | undefined): string {
  const normalized = String(dataType || '').trim().toLowerCase()
  if (normalized === 'date') return 'DATE'
  if (normalized === 'boolean') return 'BOOLEAN'
  if (normalized === 'number') return 'DOUBLE'
  return 'VARCHAR'
}

function tableauDatatypeForField(dataType: string | null | undefined): string {
  const normalized = String(dataType || '').trim().toLowerCase()
  if (normalized === 'date') return 'date'
  if (normalized === 'boolean') return 'boolean'
  if (normalized === 'number') return 'real'
  return 'string'
}

export function normalizeAggregation(raw: any): QuickBooksGlCreatorAggregation {
  const text = String(raw || '').trim().toUpperCase()
  return AGGREGATIONS.includes(text as QuickBooksGlCreatorAggregation) ? text as QuickBooksGlCreatorAggregation : 'SUM'
}

function normalizeVisualizationType(raw: any): QuickBooksGlCreatorVisualizationType {
  const text = String(raw || '').trim().toLowerCase()
  return text === 'bar' || text === 'line' || text === 'table' ? text : 'table'
}

export function normalizeDateGrain(raw: any): QuickBooksGlCreatorDateGrain {
  const text = String(raw || '').trim().toLowerCase()
  return DATE_GRAINS.includes(text as QuickBooksGlCreatorDateGrain) ? text as QuickBooksGlCreatorDateGrain : 'month'
}

function fieldLabel(fieldId: string): string {
  return FIELD_BY_ID.get(fieldId)?.label || fieldId.replace(/_/g, ' ')
}

function uniqueAlias(base: string, used: Set<string>): string {
  const clean = String(base || 'Field').trim() || 'Field'
  if (!used.has(clean)) {
    used.add(clean)
    return clean
  }
  for (let index = 2; index < 1000; index += 1) {
    const candidate = `${clean} ${index}`
    if (!used.has(candidate)) {
      used.add(candidate)
      return candidate
    }
  }
  const candidate = `${clean} ${used.size + 1}`
  used.add(candidate)
  return candidate
}

export function sanitizeShelfField(raw: any, shelf: 'rows' | 'columns'): ShelfField | null {
  const fieldName = String(raw?.field_name || raw?.field || '').trim()
  const field = FIELD_BY_ID.get(fieldName)
  if (!field || field.role !== 'dimension') return null
  return {
    field_name: field.id,
    display_name: field.label,
    role: 'dimension',
    data_type: field.dataType,
    shelf,
    is_discrete: true,
    type: 'discrete',
    time_grain: field.dataType === 'date' ? normalizeDateGrain(raw?.time_grain || 'month') : null,
    calendar_type: field.dataType === 'date' ? 'standard_gregorian' : null,
  }
}

function sanitizeShelfFields(raw: any, shelf: 'rows' | 'columns'): ShelfField[] {
  return (Array.isArray(raw) ? raw : [])
    .map((field) => sanitizeShelfField(field, shelf))
    .filter((field): field is ShelfField => Boolean(field))
}

export function defaultQuickBooksGlCreatorConfig(): QuickBooksGlCreatorConfig {
  return {
    rows: [
      sanitizeShelfField({ field_name: 'account' }, 'rows')!,
      sanitizeShelfField({ field_name: 'class' }, 'rows')!,
      sanitizeShelfField({ field_name: 'customer' }, 'rows')!,
    ],
    columns: [sanitizeShelfField({ field_name: 'txn_date', time_grain: 'month' }, 'columns')!],
    measure: 'amount',
    aggregation: 'SUM',
    dateGrains: { txn_date: 'month' },
    visualizationType: 'table',
  }
}

export function normalizeQuickBooksGlCreatorConfig(raw: any): QuickBooksGlCreatorConfig {
  const fallback = defaultQuickBooksGlCreatorConfig()
  const measure = FIELD_BY_ID.get(String(raw?.measure || '').trim())?.role === 'measure'
    ? String(raw.measure).trim()
    : fallback.measure
  const rows = sanitizeShelfFields(raw?.rows, 'rows')
  const columns = sanitizeShelfFields(raw?.columns, 'columns')
  const dateGrains: Record<string, QuickBooksGlCreatorDateGrain> = {}
  const rawGrains = raw?.dateGrains && typeof raw.dateGrains === 'object' ? raw.dateGrains : {}
  for (const [key, value] of Object.entries(rawGrains)) {
    if (FIELD_BY_ID.get(key)?.dataType === 'date') dateGrains[key] = normalizeDateGrain(value)
  }
  for (const shelfField of [...rows, ...columns]) {
    if (FIELD_BY_ID.get(shelfField.field_name)?.dataType === 'date') {
      dateGrains[shelfField.field_name] = normalizeDateGrain(shelfField.time_grain || dateGrains[shelfField.field_name] || 'month')
    }
  }
  return {
    rows: rows.length ? rows : fallback.rows,
    columns: columns.length ? columns : fallback.columns,
    measure,
    aggregation: normalizeAggregation(raw?.aggregation),
    dateGrains: Object.keys(dateGrains).length ? dateGrains : fallback.dateGrains,
    visualizationType: normalizeVisualizationType(raw?.visualizationType),
  }
}

export type QuickBooksGlCreatorPendingOpenRequest = {
  workbookId: string | null
  config: QuickBooksGlCreatorConfig
  createdAt: number
  source: string | null
}

type GlCreatorPendingStorage = Pick<Storage, 'getItem' | 'setItem' | 'removeItem'>

const normalizePendingWorkbookId = (value: unknown): string | null => {
  const text = String(value ?? '').trim()
  return text || null
}

export function writeQuickBooksGlCreatorPendingOpen(
  storage: GlCreatorPendingStorage | null | undefined,
  input: {
    workbookId?: string | null
    config?: any
    source?: string | null
    nowMs?: number
  },
): QuickBooksGlCreatorPendingOpenRequest | null {
  if (!storage || typeof storage.setItem !== 'function') return null
  const request: QuickBooksGlCreatorPendingOpenRequest = {
    workbookId: normalizePendingWorkbookId(input.workbookId),
    config: normalizeQuickBooksGlCreatorConfig(input.config),
    createdAt: Number.isFinite(Number(input.nowMs)) ? Number(input.nowMs) : Date.now(),
    source: String(input.source ?? '').trim() || null,
  }
  try {
    storage.setItem(QUICKBOOKS_GL_CREATOR_PENDING_OPEN_STORAGE_KEY, JSON.stringify(request))
  } catch {
    return null
  }
  return request
}

export function readQuickBooksGlCreatorPendingOpen(
  storage: GlCreatorPendingStorage | null | undefined,
  input: {
    workbookId?: string | null
    nowMs?: number
    maxAgeMs?: number
    removeExpired?: boolean
  } = {},
): QuickBooksGlCreatorPendingOpenRequest | null {
  if (!storage || typeof storage.getItem !== 'function') return null
  const raw = (() => {
    try {
      return String(storage.getItem(QUICKBOOKS_GL_CREATOR_PENDING_OPEN_STORAGE_KEY) || '').trim()
    } catch {
      return ''
    }
  })()
  if (!raw) return null
  let parsed: any = null
  try {
    parsed = JSON.parse(raw)
  } catch {
    if (typeof storage.removeItem === 'function') {
      try { storage.removeItem(QUICKBOOKS_GL_CREATOR_PENDING_OPEN_STORAGE_KEY) } catch { }
    }
    return null
  }
  const requestWorkbookId = normalizePendingWorkbookId(parsed?.workbookId)
  const activeWorkbookId = normalizePendingWorkbookId(input.workbookId)
  if (requestWorkbookId && activeWorkbookId && requestWorkbookId !== activeWorkbookId) return null
  const createdAt = Number(parsed?.createdAt)
  const nowMs = Number.isFinite(Number(input.nowMs)) ? Number(input.nowMs) : Date.now()
  const maxAgeMs = Math.max(1, Number.isFinite(Number(input.maxAgeMs)) ? Number(input.maxAgeMs) : QUICKBOOKS_GL_CREATOR_PENDING_OPEN_MAX_AGE_MS)
  if (!Number.isFinite(createdAt) || createdAt <= 0 || nowMs - createdAt > maxAgeMs) {
    if (input.removeExpired !== false && typeof storage.removeItem === 'function') {
      try { storage.removeItem(QUICKBOOKS_GL_CREATOR_PENDING_OPEN_STORAGE_KEY) } catch { }
    }
    return null
  }
  return {
    workbookId: requestWorkbookId,
    config: normalizeQuickBooksGlCreatorConfig(parsed?.config),
    createdAt,
    source: String(parsed?.source ?? '').trim() || null,
  }
}

export function consumeQuickBooksGlCreatorPendingOpen(
  storage: GlCreatorPendingStorage | null | undefined,
  input: {
    workbookId?: string | null
    nowMs?: number
    maxAgeMs?: number
  } = {},
): QuickBooksGlCreatorPendingOpenRequest | null {
  const request = readQuickBooksGlCreatorPendingOpen(storage, input)
  if (!request) return null
  if (storage && typeof storage.removeItem === 'function') {
    try { storage.removeItem(QUICKBOOKS_GL_CREATOR_PENDING_OPEN_STORAGE_KEY) } catch { }
  }
  return request
}

function buildQuickBooksGlCreatorDataFields() {
  return QUICKBOOKS_GL_CREATOR_FIELDS.map((field) => ({
    id: field.id,
    label: field.label,
    data_type: creatorDataTypeForField(field.dataType),
  }))
}

export function buildQuickBooksGlCreatorSourceSql(
  tableName = QUICKBOOKS_GL_CREATOR_TABLE,
  sourceColumns?: QuickBooksGlCreatorSourceColumn[] | null,
): string {
  const sourceColumnLookup = buildSourceColumnLookup(sourceColumns)
  const selectParts = QUICKBOOKS_GL_CANONICAL_SOURCE_FIELDS.map((field) => {
    const targetIdent = quoteDuckDbIdent(field.id)
    const sourceColumn = resolveQuickBooksGlSourceColumn(
      sourceColumnLookup,
      field.id,
      QUICKBOOKS_GL_SOURCE_COLUMN_ALIASES[field.id] || [],
    )
    const sourceExpr = sourceColumn ? quoteDuckDbIdent(sourceColumn) : missingQuickBooksGlFieldSql(field, sourceColumnLookup || new Map())
    if (field.dataType === 'date') return `TRY_CAST(${sourceExpr} AS DATE) AS ${targetIdent}`
    if (field.dataType === 'number') return `TRY_CAST(${sourceExpr} AS DOUBLE) AS ${targetIdent}`
    if (field.dataType === 'boolean') return `TRY_CAST(${sourceExpr} AS BOOLEAN) AS ${targetIdent}`
    return `CAST(${sourceExpr} AS VARCHAR) AS ${targetIdent}`
  })
  return `SELECT\n  ${selectParts.join(',\n  ')}\nFROM ${quoteDuckDbRelation(tableName)}`
}

function buildQuickBooksGlCreatorShelfPill(args: {
  fieldName: string
  shelf: 'rows' | 'columns' | 'text'
  index: number
  aggregation?: string | null
  timeGrain?: string | null
}) {
  const catalogField = FIELD_BY_ID.get(args.fieldName)
  const isMeasure = catalogField?.role === 'measure' || Boolean(args.aggregation)
  return {
    id: `${QUICKBOOKS_GL_CREATOR_DATASOURCE_ID}:${args.shelf}:${args.fieldName}:${args.index}`,
    source_id: QUICKBOOKS_GL_CREATOR_DATASOURCE_ID,
    field: args.fieldName,
    field_name: args.fieldName,
    shelf: args.shelf,
    alias: catalogField?.label || args.fieldName,
    label: catalogField?.label || args.fieldName,
    aggregation: args.aggregation ?? null,
    time_grain: args.timeGrain ?? null,
    is_measure: isMeasure,
    is_discrete: !isMeasure,
    data_type: creatorDataTypeForField(catalogField?.dataType),
  }
}

export function buildQuickBooksGlCreatorVisualSpec(
  rawConfig?: QuickBooksGlCreatorConfig | null,
  tableName = QUICKBOOKS_GL_CREATOR_TABLE,
) {
  const config = normalizeQuickBooksGlCreatorConfig(rawConfig || defaultQuickBooksGlCreatorConfig())
  const rows = config.rows.map((field, index) => buildQuickBooksGlCreatorShelfPill({
    fieldName: field.field_name,
    shelf: 'rows',
    index,
    timeGrain: field.time_grain || config.dateGrains[field.field_name] || null,
  }))
  const columns = config.columns.map((field, index) => buildQuickBooksGlCreatorShelfPill({
    fieldName: field.field_name,
    shelf: 'columns',
    index,
    timeGrain: field.time_grain || config.dateGrains[field.field_name] || null,
  }))
  const measure = buildQuickBooksGlCreatorShelfPill({
    fieldName: config.measure,
    shelf: 'text',
    index: 0,
    aggregation: normalizeAggregation(config.aggregation).toLowerCase(),
  })
  const measureValue = {
    ...measure,
    id: `${QUICKBOOKS_GL_CREATOR_DATASOURCE_ID}:measureValues:${config.measure}:0`,
    shelf: 'measureValues',
    role: 'measure',
    display_name: measure.label,
  }
  const mark = config.visualizationType === 'bar'
    ? 'bar'
    : config.visualizationType === 'line'
      ? 'line'
      : 'text'

  return {
    data_pane: {
      sources: [{
        id: QUICKBOOKS_GL_CREATOR_DATASOURCE_ID,
        name: QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME,
        kind: 'table',
        reference: tableName,
        aliases: [tableName],
        source_aliases: [tableName, QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME, 'QuickBooks GL Detail'],
        fields: buildQuickBooksGlCreatorDataFields(),
      }],
      calculated_fields: [],
      parameters: [],
      sets: [],
      field_overrides: [],
    },
    shelves: { rows, columns },
    marks: {
      mark,
      text: [measure],
    },
    measureValues: [measureValue],
    filters: [],
    limit: null,
    title: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
    view: {
      fit: 'standard',
      field_labels: { rows: true, cols: true },
    },
  }
}

function buildQuickBooksGlCreatorDatasource(tableName = QUICKBOOKS_GL_CREATOR_TABLE) {
  return {
    name: QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME,
    caption: QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME,
    datasource_id: QUICKBOOKS_GL_CREATOR_DATASOURCE_ID,
    has_connection: true,
    inline: true,
    connection_type: 'duckdb',
    table: tableName,
    reference: tableName,
    connections: [{
      class: 'duckdb',
      dbname: ':memory:',
      relation: tableName,
      table: tableName,
    }],
  }
}

function buildQuickBooksGlCreatorFieldDictionaryColumns() {
  return QUICKBOOKS_GL_CREATOR_FIELDS.map((field) => {
    const datatype = tableauDatatypeForField(field.dataType)
    return {
      datasource: QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME,
      datasource_id: QUICKBOOKS_GL_CREATOR_DATASOURCE_ID,
      name: field.id,
      caption: field.label,
      datatype,
      user_datatype: datatype,
      role: field.role,
      type: field.role === 'measure' ? 'quantitative' : 'nominal',
      default_aggregation: field.role === 'measure' ? 'Sum' : null,
    }
  })
}

function buildQuickBooksGlCreatorWorksheetSpec(
  rawConfig?: QuickBooksGlCreatorConfig | null,
  tableName = QUICKBOOKS_GL_CREATOR_TABLE,
) {
  const spec = buildQuickBooksGlCreatorVisualSpec(rawConfig, tableName)
  const tableauSource = {
    worksheet_name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
    primary_datasource: QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME,
    worksheet_datasources: [QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME],
    local_table: tableName,
    source_kind: 'quickbooks-gl-detail',
  }
  return {
    worksheet_name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
    internal_name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
    kind: 'viz',
    source: { tableau: tableauSource },
    worksheet_spec: {
      kind: 'viz',
      name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
      worksheet_name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
      internal_name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
      source: { tableau: tableauSource },
      spec,
      view: spec.view,
    },
  }
}

function isQuickBooksGlDatasourceEntry(entry: any): boolean {
  const candidates = [
    entry?.datasource_id,
    entry?.id,
    entry?.name,
    entry?.caption,
    entry?.reference,
    entry?.table,
  ]
  return candidates.some((candidate) => {
    const text = String(candidate ?? '').trim()
    return text === QUICKBOOKS_GL_CREATOR_DATASOURCE_ID ||
      text === QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME ||
      text === QUICKBOOKS_GL_CREATOR_TABLE
  })
}

function isQuickBooksGlFieldDictionaryColumn(entry: any): boolean {
  return String(entry?.datasource_id ?? '').trim() === QUICKBOOKS_GL_CREATOR_DATASOURCE_ID ||
    String(entry?.datasource ?? '').trim() === QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME
}

function worksheetNameOf(entry: any): string {
  return String(entry?.name ?? entry?.worksheet_name ?? entry?.internal_name ?? '').trim()
}

function normalizeQuickBooksGlCreatorFieldKey(raw: any): string {
  return String(raw ?? '').trim().toLowerCase().replace(/[^a-z0-9]+/g, '')
}

function getQuickBooksGlCreatorWorksheetVisualSpec(entry: any): any | null {
  const raw = entry?.worksheet_spec
  const worksheetSpec = (() => {
    if (!raw) return null
    if (typeof raw === 'string') {
      try {
        return JSON.parse(raw)
      } catch {
        return null
      }
    }
    return raw && typeof raw === 'object' ? raw : null
  })()
  const spec = worksheetSpec?.spec
  return spec && typeof spec === 'object' ? spec : null
}

function isLegacyAccountOnlyQuickBooksGlCreatorSpec(entry: any): boolean {
  if (!entry || worksheetNameOf(entry) !== QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME) return false
  const visualSpec = getQuickBooksGlCreatorWorksheetVisualSpec(entry)
  if (!visualSpec) return false
  const rows = Array.isArray(visualSpec?.shelves?.rows) ? visualSpec.shelves.rows : []
  const columns = Array.isArray(visualSpec?.shelves?.columns) ? visualSpec.shelves.columns : []
  const textMarks = Array.isArray(visualSpec?.marks?.text)
    ? visualSpec.marks.text
    : (visualSpec?.marks?.text ? [visualSpec.marks.text] : [])
  const rowKeys = rows.map((row: any) => normalizeQuickBooksGlCreatorFieldKey(row?.field_name ?? row?.field))
  const columnKeys = columns.map((column: any) => normalizeQuickBooksGlCreatorFieldKey(column?.field_name ?? column?.field))
  const textKeys = textMarks.map((mark: any) => normalizeQuickBooksGlCreatorFieldKey(mark?.field_name ?? mark?.field))
  return (
    rowKeys.length === 1 &&
    rowKeys[0] === 'account' &&
    columnKeys.length === 1 &&
    columnKeys[0] === 'txndate' &&
    textKeys.includes('amount')
  )
}

export function migrateQuickBooksGlCreatorWorkbookIr(workbookIrRaw: any): any {
  if (!workbookIrRaw || typeof workbookIrRaw !== 'object') return workbookIrRaw
  const currentVersion = Number((workbookIrRaw as any)?.meta?.quickbooks_gl_creator_default_config_version ?? 0) || 0
  if (currentVersion >= QUICKBOOKS_GL_CREATOR_DEFAULT_CONFIG_VERSION) return workbookIrRaw
  const specs = Array.isArray((workbookIrRaw as any).worksheet_specs)
    ? ((workbookIrRaw as any).worksheet_specs as any[])
    : []
  const glSpec = specs.find((entry: any) => worksheetNameOf(entry) === QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME) || null
  if (!isLegacyAccountOnlyQuickBooksGlCreatorSpec(glSpec)) return workbookIrRaw
  const tableName = String((workbookIrRaw as any)?.meta?.quickbooks_gl_creator_table || QUICKBOOKS_GL_CREATOR_TABLE).trim() || QUICKBOOKS_GL_CREATOR_TABLE
  const migrated = upsertQuickBooksGlCreatorWorksheet(workbookIrRaw, defaultQuickBooksGlCreatorConfig(), tableName)
  return {
    ...migrated,
    meta: {
      ...((migrated as any)?.meta && typeof (migrated as any).meta === 'object' ? (migrated as any).meta : {}),
      quickbooks_gl_creator_default_config_version: QUICKBOOKS_GL_CREATOR_DEFAULT_CONFIG_VERSION,
    },
  }
}

export function upsertQuickBooksGlCreatorWorksheet(
  workbookIrRaw: any,
  rawConfig?: QuickBooksGlCreatorConfig | null,
  tableName = QUICKBOOKS_GL_CREATOR_TABLE,
) {
  const workbookIr = workbookIrRaw && typeof workbookIrRaw === 'object'
    ? deepCloneSafe(workbookIrRaw)
    : {}
  const now = new Date().toISOString()
  const worksheets = Array.isArray(workbookIr.worksheets) ? workbookIr.worksheets : []
  const specs = Array.isArray(workbookIr.worksheet_specs) ? workbookIr.worksheet_specs : []
  const maxTabOrder = worksheets.reduce((max: number, worksheet: any) => {
    const next = Number(worksheet?.tab_order ?? -1)
    return Number.isFinite(next) ? Math.max(max, next) : max
  }, -1)
  const hasWorksheet = worksheets.some((worksheet: any) => worksheetNameOf(worksheet) === QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME)
  const nextWorksheet = {
    name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
    worksheet_name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
    internal_name: QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME,
    sheet_type: 'worksheet',
    source_kind: 'quickbooks-gl-detail',
    primary_datasource: QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME,
    worksheet_datasources: [QUICKBOOKS_GL_CREATOR_DATASOURCE_NAME],
    tab_order: hasWorksheet
      ? (worksheets.find((worksheet: any) => worksheetNameOf(worksheet) === QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME)?.tab_order ?? maxTabOrder)
      : maxTabOrder + 1,
  }
  const nextSpec = buildQuickBooksGlCreatorWorksheetSpec(rawConfig, tableName)
  const existingFieldDictionary = workbookIr.field_dictionary && typeof workbookIr.field_dictionary === 'object'
    ? workbookIr.field_dictionary
    : {}
  const existingColumns = Array.isArray(existingFieldDictionary.columns) ? existingFieldDictionary.columns : []
  const existingColumnInstances = Array.isArray(existingFieldDictionary.column_instances) ? existingFieldDictionary.column_instances : []

  return {
    ...workbookIr,
    name: String(workbookIr.name || workbookIr?.meta?.name || '').trim() || 'Workbook',
    datasources: [
      ...(Array.isArray(workbookIr.datasources) ? workbookIr.datasources : []).filter((entry: any) => !isQuickBooksGlDatasourceEntry(entry)),
      buildQuickBooksGlCreatorDatasource(tableName),
    ],
    worksheets: [
      ...worksheets.filter((worksheet: any) => worksheetNameOf(worksheet) !== QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME),
      nextWorksheet,
    ],
    worksheet_specs: [
      ...specs.filter((spec: any) => worksheetNameOf(spec) !== QUICKBOOKS_GL_CREATOR_WORKSHEET_NAME),
      nextSpec,
    ],
    dashboards: Array.isArray(workbookIr.dashboards) ? workbookIr.dashboards : [],
    parameters: Array.isArray(workbookIr.parameters) ? workbookIr.parameters : [],
    actions: Array.isArray(workbookIr.actions) ? workbookIr.actions : [],
    field_dictionary: {
      ...existingFieldDictionary,
      columns: [
        ...existingColumns.filter((entry: any) => !isQuickBooksGlFieldDictionaryColumn(entry)),
        ...buildQuickBooksGlCreatorFieldDictionaryColumns(),
      ],
      column_instances: existingColumnInstances.filter((entry: any) => !isQuickBooksGlFieldDictionaryColumn(entry)),
    },
    meta: {
      ...(workbookIr.meta && typeof workbookIr.meta === 'object' ? workbookIr.meta : {}),
      updated_at: now,
      quickbooks_gl_creator_table: tableName,
      quickbooks_gl_creator_default_config_version: QUICKBOOKS_GL_CREATOR_DEFAULT_CONFIG_VERSION,
    },
  }
}

function dateDimensionSql(fieldId: string, grain: QuickBooksGlCreatorDateGrain): string {
  const dateExpr = `TRY_CAST(${quoteDuckDbIdent(fieldId)} AS DATE)`
  if (grain === 'year') return `COALESCE(CAST(date_part('year', ${dateExpr}) AS VARCHAR), '(Blank)')`
  if (grain === 'quarter') return `COALESCE(CAST(date_part('year', ${dateExpr}) AS VARCHAR) || '-Q' || CAST(date_part('quarter', ${dateExpr}) AS VARCHAR), '(Blank)')`
  if (grain === 'week') return `COALESCE(strftime(date_trunc('week', ${dateExpr}), '%Y-%m-%d'), '(Blank)')`
  if (grain === 'day') return `COALESCE(strftime(${dateExpr}, '%Y-%m-%d'), '(Blank)')`
  return `COALESCE(strftime(date_trunc('month', ${dateExpr}), '%Y-%m'), '(Blank)')`
}

function dimensionSql(field: ShelfField, dateGrains: Record<string, QuickBooksGlCreatorDateGrain>): string {
  const catalogField = FIELD_BY_ID.get(field.field_name)
  if (!catalogField || catalogField.role !== 'dimension') {
    throw new Error(`Unsupported GL dimension field: ${field.field_name}`)
  }
  if (catalogField.dataType === 'date') {
    return dateDimensionSql(catalogField.id, normalizeDateGrain(field.time_grain || dateGrains[catalogField.id] || 'month'))
  }
  if (catalogField.dataType === 'boolean') {
    return `CASE WHEN TRY_CAST(${quoteDuckDbIdent(catalogField.id)} AS BOOLEAN) THEN 'Yes' ELSE 'No' END`
  }
  return `COALESCE(NULLIF(TRIM(CAST(${quoteDuckDbIdent(catalogField.id)} AS VARCHAR)), ''), '(Blank)')`
}

function measureSql(measure: string, aggregation: QuickBooksGlCreatorAggregation): { expression: string; alias: string } {
  const field = FIELD_BY_ID.get(measure)
  if (aggregation !== 'COUNT' && (!field || field.role !== 'measure')) {
    throw new Error(`Unsupported GL measure field: ${measure}`)
  }
  if (aggregation === 'COUNT') return { expression: 'COUNT(*)', alias: 'COUNT Rows' }
  const numeric = `TRY_CAST(${quoteDuckDbIdent(measure)} AS DOUBLE)`
  if (aggregation === 'SUM') return { expression: `SUM(COALESCE(${numeric}, 0))`, alias: `SUM ${fieldLabel(measure)}` }
  return { expression: `${aggregation}(${numeric})`, alias: `${aggregation} ${fieldLabel(measure)}` }
}

export function buildQuickBooksGlCreatorQuery(
  config: QuickBooksGlCreatorConfig,
  tableName = QUICKBOOKS_GL_CREATOR_TABLE,
): QuickBooksGlCreatorQuery {
  const normalized: QuickBooksGlCreatorConfig = {
    rows: Array.isArray(config?.rows) ? config.rows : [],
    columns: Array.isArray(config?.columns) ? config.columns : [],
    measure: String(config?.measure || 'amount').trim(),
    aggregation: normalizeAggregation(config?.aggregation),
    dateGrains: config?.dateGrains && typeof config.dateGrains === 'object' ? config.dateGrains : {},
    visualizationType: normalizeVisualizationType(config?.visualizationType),
  }
  const usedAliases = new Set<string>()
  const dimensions = [...normalized.rows, ...normalized.columns]
  const selectParts: string[] = []
  const dimensionAliases: string[] = []

  dimensions.forEach((field) => {
    const catalogField = FIELD_BY_ID.get(field.field_name)
    if (!catalogField || catalogField.role !== 'dimension') throw new Error(`Unsupported GL dimension field: ${field.field_name}`)
    const grain = catalogField.dataType === 'date' ? normalizeDateGrain(field.time_grain || normalized.dateGrains[field.field_name] || 'month') : null
    const alias = uniqueAlias(grain ? `${catalogField.label} (${grain})` : catalogField.label, usedAliases)
    dimensionAliases.push(alias)
    selectParts.push(`${dimensionSql(field, normalized.dateGrains)} AS ${quoteDuckDbIdent(alias)}`)
  })

  const measure = measureSql(normalized.measure, normalized.aggregation)
  const measureAlias = uniqueAlias(measure.alias, usedAliases)
  selectParts.push(`${measure.expression} AS ${quoteDuckDbIdent(measureAlias)}`)

  const groupBy = dimensionAliases.length ? `\nGROUP BY ${dimensionAliases.map((_, index) => String(index + 1)).join(', ')}` : ''
  const orderBy = dimensionAliases.length
    ? `\nORDER BY ${dimensionAliases.map((_, index) => String(index + 1)).join(', ')}`
    : `\nORDER BY ${quoteDuckDbIdent(measureAlias)} DESC`

  return {
    sql: `SELECT\n  ${selectParts.join(',\n  ')}\nFROM ${quoteDuckDbIdent(tableName)}${groupBy}${orderBy}\nLIMIT ${QUICKBOOKS_GL_CREATOR_PREVIEW_LIMIT}`,
    dimensionAliases,
    measureAlias,
  }
}
